import { useState, useMemo } from 'react';
import { Globe, Search } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Common timezones grouped by region
const TIMEZONE_GROUPS = {
  'Europe': [
    { value: 'Europe/Paris', label: 'Paris', offset: '+1/+2' },
    { value: 'Europe/London', label: 'Londres', offset: '+0/+1' },
    { value: 'Europe/Berlin', label: 'Berlin', offset: '+1/+2' },
    { value: 'Europe/Madrid', label: 'Madrid', offset: '+1/+2' },
    { value: 'Europe/Rome', label: 'Rome', offset: '+1/+2' },
    { value: 'Europe/Amsterdam', label: 'Amsterdam', offset: '+1/+2' },
    { value: 'Europe/Brussels', label: 'Bruxelles', offset: '+1/+2' },
    { value: 'Europe/Zurich', label: 'Zürich', offset: '+1/+2' },
    { value: 'Europe/Lisbon', label: 'Lisbonne', offset: '+0/+1' },
    { value: 'Europe/Warsaw', label: 'Varsovie', offset: '+1/+2' },
    { value: 'Europe/Moscow', label: 'Moscou', offset: '+3' },
  ],
  'Amérique du Nord': [
    { value: 'America/New_York', label: 'New York', offset: '-5/-4' },
    { value: 'America/Chicago', label: 'Chicago', offset: '-6/-5' },
    { value: 'America/Denver', label: 'Denver', offset: '-7/-6' },
    { value: 'America/Los_Angeles', label: 'Los Angeles', offset: '-8/-7' },
    { value: 'America/Toronto', label: 'Toronto', offset: '-5/-4' },
    { value: 'America/Vancouver', label: 'Vancouver', offset: '-8/-7' },
    { value: 'America/Montreal', label: 'Montréal', offset: '-5/-4' },
    { value: 'America/Mexico_City', label: 'Mexico', offset: '-6/-5' },
  ],
  'Amérique du Sud': [
    { value: 'America/Sao_Paulo', label: 'São Paulo', offset: '-3' },
    { value: 'America/Buenos_Aires', label: 'Buenos Aires', offset: '-3' },
    { value: 'America/Lima', label: 'Lima', offset: '-5' },
    { value: 'America/Bogota', label: 'Bogotá', offset: '-5' },
    { value: 'America/Santiago', label: 'Santiago', offset: '-4/-3' },
  ],
  'Asie': [
    { value: 'Asia/Tokyo', label: 'Tokyo', offset: '+9' },
    { value: 'Asia/Shanghai', label: 'Shanghai', offset: '+8' },
    { value: 'Asia/Hong_Kong', label: 'Hong Kong', offset: '+8' },
    { value: 'Asia/Singapore', label: 'Singapour', offset: '+8' },
    { value: 'Asia/Seoul', label: 'Séoul', offset: '+9' },
    { value: 'Asia/Dubai', label: 'Dubaï', offset: '+4' },
    { value: 'Asia/Bangkok', label: 'Bangkok', offset: '+7' },
    { value: 'Asia/Kolkata', label: 'Mumbai', offset: '+5:30' },
    { value: 'Asia/Jakarta', label: 'Jakarta', offset: '+7' },
  ],
  'Afrique': [
    { value: 'Africa/Cairo', label: 'Le Caire', offset: '+2' },
    { value: 'Africa/Johannesburg', label: 'Johannesburg', offset: '+2' },
    { value: 'Africa/Lagos', label: 'Lagos', offset: '+1' },
    { value: 'Africa/Casablanca', label: 'Casablanca', offset: '+0/+1' },
    { value: 'Africa/Nairobi', label: 'Nairobi', offset: '+3' },
    { value: 'Africa/Tunis', label: 'Tunis', offset: '+1' },
  ],
  'Océanie': [
    { value: 'Australia/Sydney', label: 'Sydney', offset: '+10/+11' },
    { value: 'Australia/Melbourne', label: 'Melbourne', offset: '+10/+11' },
    { value: 'Australia/Perth', label: 'Perth', offset: '+8' },
    { value: 'Pacific/Auckland', label: 'Auckland', offset: '+12/+13' },
  ],
  'Moyen-Orient': [
    { value: 'Asia/Jerusalem', label: 'Jérusalem', offset: '+2/+3' },
    { value: 'Asia/Beirut', label: 'Beyrouth', offset: '+2/+3' },
    { value: 'Asia/Riyadh', label: 'Riyad', offset: '+3' },
    { value: 'Asia/Tehran', label: 'Téhéran', offset: '+3:30' },
  ],
};

// Flatten for search
const ALL_TIMEZONES = Object.entries(TIMEZONE_GROUPS).flatMap(([region, zones]) =>
  zones.map(zone => ({ ...zone, region }))
);

interface TimezoneSelectProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly className?: string;
  readonly disabled?: boolean;
}

export function TimezoneSelect({ value, onChange, className, disabled }: TimezoneSelectProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const selectedTimezone = useMemo(() => {
    return ALL_TIMEZONES.find(tz => tz.value === value);
  }, [value]);

  const filteredTimezones = useMemo(() => {
    if (!searchQuery.trim()) return TIMEZONE_GROUPS;
    
    const query = searchQuery.toLowerCase();
    const filtered: Record<string, typeof ALL_TIMEZONES> = {};
    
    ALL_TIMEZONES.forEach(tz => {
      if (
        tz.label.toLowerCase().includes(query) ||
        tz.value.toLowerCase().includes(query) ||
        tz.region.toLowerCase().includes(query)
      ) {
        if (!filtered[tz.region]) {
          filtered[tz.region] = [];
        }
        filtered[tz.region].push(tz);
      }
    });
    
    return filtered;
  }, [searchQuery]);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className={cn("w-full pointer-events-auto", className)}>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
          {selectedTimezone ? (
            <span className="truncate">
              {selectedTimezone.label} (UTC{selectedTimezone.offset})
            </span>
          ) : (
            <SelectValue placeholder="Sélectionner un fuseau horaire" />
          )}
        </div>
      </SelectTrigger>
      <SelectContent className="z-[9999] pointer-events-auto p-0 overflow-hidden">
        {/* Search input - fixed at top */}
        <div className="p-2 border-b border-border bg-popover">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher une ville..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 h-8"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        
        {/* Scrollable timezone list */}
        <div className="max-h-60 overflow-y-auto">
          {Object.entries(filteredTimezones).map(([region, zones]) => (
            <div key={region}>
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {region}
              </div>
              {zones.map((tz) => (
                <SelectItem
                  key={tz.value}
                  value={tz.value}
                  className="pl-4"
                >
                  <div className="flex items-center justify-between w-full gap-2">
                    <span>{tz.label}</span>
                    <span className="text-xs text-muted-foreground">UTC{tz.offset}</span>
                  </div>
                </SelectItem>
              ))}
            </div>
          ))}
          
          {Object.keys(filteredTimezones).length === 0 && (
            <div className="px-4 py-6 text-center text-muted-foreground text-sm">
              Aucun fuseau horaire trouvé
            </div>
          )}
        </div>
      </SelectContent>
    </Select>
  );
}

// Export timezone list for use elsewhere
export { ALL_TIMEZONES, TIMEZONE_GROUPS };
